import React from "react";
import { Button, Typography } from "design_system/src";
import { LenderEnum, loanTypeValues } from "models/inventory/enums/vehicle.enums";
import { ReactComponent as ChevronRight } from "design_system/src/static/icons/right-md.svg";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import useAlliance from "hooks/useAlliance";
import { setApplicationPartnership, setApplicationVehicle } from "store/actions/application";
import { TVehicle } from "models/inventory/interfaces/vehicle.interface";

interface LenderSummaryProps {
  type: LenderEnum;
  vehicle?: TVehicle;
}

const LenderSummary: React.FC<LenderSummaryProps> = ({ type, vehicle }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { allianceUrl } = useAlliance();

  const openFinancialForm = () => {
    if (vehicle) {
      dispatch(setApplicationVehicle(vehicle));
      dispatch(setApplicationPartnership(type));
      navigate(allianceUrl ? `/${allianceUrl}/financia-tu-moto` : "/financia-tu-moto");
    }
  };

  return (
    <div>
      <Typography
        scale="xsmall"
        weight="400"
        className="text_neutral_700 p_y_xs"
      >
        *El precio puede variar según tipo de financiamiento.
      </Typography>

      <div className="border_1 border_solid border_neutral_500 br_md p_md m_b_md">
        <Typography
          scale="medium"
          weight="600"
          className="m_b_sm"
        >
          {type}
        </Typography>
        <Typography
          scale="medium"
          weight="400"
          className="m_b_sm text_neutral_800"
        >
          {loanTypeValues[type]!.description}
        </Typography>
        {loanTypeValues[type]!.bulletPoints.map((point) => (
          <Typography
            scale="small"
            weight="400"
            className="m_b_xs text_neutral_800"
          >
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2.21213 10.7801C2.21213 10.6587 2.1888 10.5981 2.14213 10.5981L1.82013 10.7521C1.82013 10.6867 1.7828 10.6401 1.70813 10.6121L1.59613 10.5981C1.52147 10.5981 1.42813 10.6307 1.31613 10.6961C1.29747 10.6494 1.27413 10.6027 1.24613 10.5561C1.21813 10.5094 1.1948 10.4674 1.17613 10.4301C1.0548 10.1967 0.933466 9.94006 0.812133 9.66006C0.700133 9.37073 0.5928 9.0954 0.490133 8.83406C0.3968 8.57273 0.322133 8.3674 0.266133 8.21806C0.228799 8.09673 0.186799 7.91473 0.140133 7.67206C0.0934662 7.4294 0.0467995 7.1214 0.000132851 6.74806C0.1028 6.8134 0.182133 6.84606 0.238133 6.84606C0.303466 6.84606 0.364133 6.74806 0.420133 6.55206C0.448133 6.5894 0.499466 6.60806 0.574133 6.60806C0.630133 6.60806 0.672133 6.5894 0.700133 6.55206L0.924133 6.21606L1.17613 6.30006H1.19013C1.2088 6.30006 1.22747 6.29073 1.24613 6.27206C1.2648 6.2534 1.2928 6.23473 1.33013 6.21606C1.4048 6.1694 1.4608 6.14606 1.49813 6.14606L1.54013 6.16006C1.77347 6.27206 1.9228 6.4774 1.98813 6.77606C2.15613 7.4854 2.32413 7.84006 2.49213 7.84006C2.66013 7.84006 2.85613 7.66273 3.08013 7.30806C3.19213 7.13073 3.30413 6.9254 3.41613 6.69206C3.53747 6.45873 3.6588 6.1974 3.78013 5.90806C3.7988 6.02006 3.81747 6.07606 3.83613 6.07606C3.8828 6.07606 3.96213 5.9594 4.07413 5.72606C4.19547 5.49273 4.3868 5.17073 4.64813 4.76006C4.79747 4.50806 4.98413 4.2234 5.20813 3.90606C5.44147 3.58873 5.6888 3.26206 5.95013 2.92606C6.21147 2.59006 6.46347 2.27273 6.70613 1.97406C6.95813 1.6754 7.18213 1.41873 7.37813 1.20406C7.57413 0.989395 7.7188 0.849395 7.81213 0.784062C8.1668 0.541395 8.4468 0.308062 8.65213 0.0840621C8.6428 0.149395 8.6288 0.210062 8.61013 0.266062C8.6008 0.312729 8.59613 0.345395 8.59613 0.364062C8.59613 0.401395 8.6148 0.420062 8.65213 0.420062L9.04413 0.224062V0.280062C9.04413 0.354729 9.0628 0.392062 9.10013 0.392062C9.12813 0.392062 9.18413 0.350062 9.26813 0.266062C9.35213 0.182062 9.3988 0.121396 9.40813 0.0840621L9.38013 0.280062L9.85613 6.23465e-05L9.74413 0.252062C9.89347 0.149395 10.0008 0.0980619 10.0661 0.0980619C10.1035 0.0980619 10.1315 0.121395 10.1501 0.168062C10.1688 0.205395 10.1781 0.242729 10.1781 0.280062C10.1781 0.336062 10.1548 0.401395 10.1081 0.476062C10.0615 0.550729 10.0008 0.639396 9.92613 0.742062C9.87013 0.816729 9.7768 0.928729 9.64613 1.07806C9.5248 1.21806 9.33813 1.42806 9.08613 1.70806C8.83413 1.97873 8.49813 2.35673 8.07813 2.84206C7.96613 2.9634 7.79347 3.17806 7.56013 3.48606C7.3268 3.78473 7.0608 4.13473 6.76213 4.53606C6.4728 4.92806 6.18347 5.32473 5.89413 5.72606C5.6048 6.1274 5.34813 6.4914 5.12413 6.81806C4.90013 7.1354 4.74147 7.3734 4.64813 7.53206L3.78013 9.00206C3.59347 9.3194 3.43947 9.58073 3.31813 9.78606C3.1968 9.98206 3.10347 10.1174 3.03813 10.1921C2.89813 10.3601 2.74413 10.5094 2.57613 10.6401L2.45013 10.5701L2.33813 10.6401L2.21213 10.7801Z" fill="#00A63E" />
            </svg>{" "}{point}
          </Typography>
        ))}
      </div>

      <Button
        className="w_100_per"
        scale="small"
        onClick={openFinancialForm}
      >
        <Typography scale="medium" weight="600" className="flex_center">
          Continuar con mi finacimiento <ChevronRight />
        </Typography>
      </Button>
  </div>
  );
};

export default LenderSummary;
